import { ApiError } from "@/api/client";
import { fetchCnpjDetail } from "@/api/cnpj";
import { searchCnaes } from "@/api/lookups";
import { SituacaoBadge } from "@/components/SituacaoBadge";
import { socioSearchParams } from "@/components/SocioEmpresas";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/ui/states";
import {
  formatCep,
  formatCnae,
  formatCnpj,
  formatCurrencyBRL,
  formatDateBR,
  formatPhone,
  onlyDigits,
  phoneHref,
} from "@/lib/format";
import {
  FAIXA_ETARIA_LABELS,
  IDENTIFICADOR_SOCIO_LABELS,
  MATRIZ_FILIAL_LABELS,
  PORTE_LABELS,
  labelFor,
} from "@/lib/labels";
import type { CnpjDetail as CnpjDetailData } from "@/types/api";
import { ArrowUpRight, Building2, Contact, FileText, MapPin, Users } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

interface CnpjDetailProps {
  cnpj: string;
  /** URL de origem dos links internos (sócio, outros estabelecimentos). */
  from: string;
  /**
   * Navegação interna (drawer): clique simples num sócio chama este callback em
   * vez de navegar a página. Cmd/Ctrl+clique segue abrindo a página completa.
   */
  onOpenSocio?: (nome: string, documento: string | null) => void;
  /** Navegação interna (drawer) para outros estabelecimentos do CNPJ. */
  onOpenCnpj?: (cnpj: string) => void;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.status === 404 ? "CNPJ não encontrado." : error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Não foi possível carregar a ficha.";
}

/** Seção de detalhe (card com título e ícone). */
function Section({
  title,
  icon,
  children,
}: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-card p-5 shadow-sm">
      <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground">
        <span className="text-muted-foreground">{icon}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

/** Par rótulo/valor. */
function DetailField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{children ?? "—"}</dd>
    </div>
  );
}

/**
 * Conteúdo completo da ficha de um CNPJ (reutilizado pela página e pelo drawer).
 * Busca via `fetchCnpjDetail` (cacheada) — reaberturas são instantâneas.
 */
export function CnpjDetail({ cnpj, from, onOpenSocio, onOpenCnpj }: CnpjDetailProps) {
  const isValid = onlyDigits(cnpj).length === 14;

  const [detail, setDetail] = useState<CnpjDetailData | null>(null);
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const [cnaePrincipalDesc, setCnaePrincipalDesc] = useState<string | null>(null);

  useEffect(() => {
    if (!isValid) {
      setStatus("error");
      setError("CNPJ inválido.");
      return;
    }
    const controller = new AbortController();
    setStatus("loading");
    setError(null);
    setCnaePrincipalDesc(null);
    fetchCnpjDetail(cnpj, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) {
          return;
        }
        setDetail(data);
        setStatus("success");
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        setStatus("error");
        setError(errorMessage(err));
      });
    return () => controller.abort();
  }, [cnpj, isValid]);

  // Resolve a descrição do CNAE principal (o contrato pode não enviá-la).
  useEffect(() => {
    if (!detail) {
      return;
    }
    const provided = detail.estabelecimento.cnae_principal_descricao;
    if (provided) {
      setCnaePrincipalDesc(provided);
      return;
    }
    const code = detail.estabelecimento.cnae_principal;
    let active = true;
    searchCnaes(String(code))
      .then((list) => {
        if (!active) {
          return;
        }
        const match = list.find((item) => item.codigo === code);
        setCnaePrincipalDesc(match ? match.descricao : null);
      })
      .catch(() => {
        // Sem descrição — exibe apenas o código.
      });
    return () => {
      active = false;
    };
  }, [detail]);

  const address = useMemo(() => {
    if (!detail) {
      return null;
    }
    const est = detail.estabelecimento;
    const street = [est.tipo_logradouro, est.logradouro].filter(Boolean).join(" ");
    const line1 = [street, est.numero].filter(Boolean).join(", ");
    return {
      line1: line1 || null,
      complemento: est.complemento,
      bairro: est.bairro,
      cidade: [est.municipio_nome, est.uf].filter(Boolean).join(" / "),
      cep: est.cep,
    };
  }, [detail]);

  if (status === "loading") {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (status === "error" || !detail) {
    return (
      <ErrorState title="Não foi possível abrir a ficha" message={error ?? "Erro desconhecido."} />
    );
  }

  const { estabelecimento: est, empresa, simples, socios, outros_estabelecimentos } = detail;
  const phones = [
    formatPhone(est.ddd1, est.telefone1),
    formatPhone(est.ddd2, est.telefone2),
  ].filter((value): value is string => value !== null);

  return (
    <div className="space-y-5">
      {/* Cabeçalho */}
      <header className="rounded-lg border border-border bg-card p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <SituacaoBadge codigo={est.situacao_cadastral} />
              <Badge className="bg-slate-100 text-slate-700 ring-slate-500/20">
                {labelFor(MATRIZ_FILIAL_LABELS, est.matriz_filial)}
              </Badge>
            </div>
            <h1 className="mt-2 text-xl font-semibold text-foreground">
              {empresa.razao_social || "—"}
            </h1>
            {est.nome_fantasia ? (
              <p className="text-sm text-muted-foreground">{est.nome_fantasia}</p>
            ) : null}
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">CNPJ</p>
            <p className="font-mono text-sm font-medium text-foreground">{formatCnpj(est.cnpj)}</p>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Identificação */}
        <Section title="Identificação" icon={<Building2 className="h-4 w-4" aria-hidden="true" />}>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <DetailField label="Natureza jurídica">
              {empresa.natureza_descricao ??
                (empresa.natureza_juridica ? String(empresa.natureza_juridica) : "—")}
            </DetailField>
            <DetailField label="Porte">{labelFor(PORTE_LABELS, empresa.porte)}</DetailField>
            <DetailField label="Capital social">
              {formatCurrencyBRL(empresa.capital_social)}
            </DetailField>
            <DetailField label="Data de abertura">
              {formatDateBR(est.data_inicio_atividade)}
            </DetailField>
            <DetailField label="Matriz / Filial">
              {labelFor(MATRIZ_FILIAL_LABELS, est.matriz_filial)}
            </DetailField>
            {empresa.ente_federativo ? (
              <DetailField label="Ente federativo">{empresa.ente_federativo}</DetailField>
            ) : null}
            {est.situacao_especial ? (
              <DetailField label="Situação especial">
                {est.situacao_especial} ({formatDateBR(est.data_situacao_especial)})
              </DetailField>
            ) : null}
            {est.motivo_descricao ? (
              <DetailField label="Motivo da situação">{est.motivo_descricao}</DetailField>
            ) : null}
          </dl>
        </Section>

        {/* Endereço */}
        <Section title="Endereço" icon={<MapPin className="h-4 w-4" aria-hidden="true" />}>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <DetailField label="Logradouro">{address?.line1}</DetailField>
            <DetailField label="Complemento">{address?.complemento}</DetailField>
            <DetailField label="Bairro">{address?.bairro}</DetailField>
            <DetailField label="Município / UF">{address?.cidade}</DetailField>
            <DetailField label="CEP">{formatCep(est.cep)}</DetailField>
            {est.cidade_exterior ? (
              <DetailField label="Cidade no exterior">
                {est.cidade_exterior}
                {est.pais_nome ? ` — ${est.pais_nome}` : ""}
              </DetailField>
            ) : null}
          </dl>
        </Section>

        {/* Contatos */}
        <Section title="Contatos" icon={<Contact className="h-4 w-4" aria-hidden="true" />}>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <DetailField label="Telefones">
              {phones.length > 0 ? (
                <div className="flex flex-col gap-1">
                  <a
                    className="text-accent hover:underline"
                    href={`tel:${phoneHref(est.ddd1, est.telefone1)}`}
                  >
                    {formatPhone(est.ddd1, est.telefone1)}
                  </a>
                  {formatPhone(est.ddd2, est.telefone2) ? (
                    <a
                      className="text-accent hover:underline"
                      href={`tel:${phoneHref(est.ddd2, est.telefone2)}`}
                    >
                      {formatPhone(est.ddd2, est.telefone2)}
                    </a>
                  ) : null}
                </div>
              ) : (
                "—"
              )}
            </DetailField>
            <DetailField label="E-mail">
              {est.email ? (
                <a className="break-all text-accent hover:underline" href={`mailto:${est.email}`}>
                  {est.email.toLowerCase()}
                </a>
              ) : (
                "—"
              )}
            </DetailField>
            {formatPhone(est.ddd_fax, est.fax) ? (
              <DetailField label="Fax">{formatPhone(est.ddd_fax, est.fax)}</DetailField>
            ) : null}
          </dl>
        </Section>

        {/* Simples / MEI */}
        <Section
          title="Simples Nacional / MEI"
          icon={<FileText className="h-4 w-4" aria-hidden="true" />}
        >
          {simples ? (
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DetailField label="Simples Nacional">
                {simples.opcao_simples ? "Optante" : "Não optante"}
              </DetailField>
              <DetailField label="Opção pelo Simples">
                {formatDateBR(simples.data_opcao_simples)}
                {simples.data_exclusao_simples
                  ? ` · exclusão ${formatDateBR(simples.data_exclusao_simples)}`
                  : ""}
              </DetailField>
              <DetailField label="MEI">{simples.opcao_mei ? "Optante" : "Não optante"}</DetailField>
              <DetailField label="Opção pelo MEI">
                {formatDateBR(simples.data_opcao_mei)}
                {simples.data_exclusao_mei
                  ? ` · exclusão ${formatDateBR(simples.data_exclusao_mei)}`
                  : ""}
              </DetailField>
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">Sem informação de Simples/MEI.</p>
          )}
        </Section>
      </div>

      {/* Atividades */}
      <Section
        title="Atividades econômicas (CNAE)"
        icon={<FileText className="h-4 w-4" aria-hidden="true" />}
      >
        <div className="space-y-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Atividade principal
            </p>
            <p className="text-sm text-foreground">
              <span className="font-mono text-muted-foreground">
                {formatCnae(est.cnae_principal)}
              </span>
              {cnaePrincipalDesc ? ` — ${cnaePrincipalDesc}` : ""}
            </p>
          </div>
          {est.cnaes_secundarios.length > 0 ? (
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Atividades secundárias ({est.cnaes_secundarios.length})
              </p>
              <ul className="space-y-1">
                {est.cnaes_secundarios.map((item) => (
                  <li key={item.codigo} className="text-sm text-foreground">
                    <span className="font-mono text-muted-foreground">
                      {formatCnae(item.codigo)}
                    </span>
                    {item.descricao ? ` — ${item.descricao}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </Section>

      {/* Quadro societário */}
      <Section
        title={`Quadro societário (${socios.length})`}
        icon={<Users className="h-4 w-4" aria-hidden="true" />}
      >
        {socios.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[40rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Nome</th>
                  <th className="px-3 py-2 font-medium">Qualificação</th>
                  <th className="px-3 py-2 font-medium">Tipo</th>
                  <th className="px-3 py-2 font-medium">Entrada</th>
                  <th className="px-3 py-2 font-medium">Faixa etária</th>
                </tr>
              </thead>
              <tbody>
                {socios.map((socio, index) => (
                  <tr
                    key={`${socio.documento ?? socio.nome}-${index}`}
                    className="border-b border-border/60"
                  >
                    <td className="px-3 py-2.5 align-top">
                      {socio.nome ? (
                        <Link
                          to={`/socio?${socioSearchParams(socio.nome, socio.documento)}`}
                          state={{ from }}
                          title="Ver todas as empresas deste sócio"
                          className="inline-flex items-center gap-1 font-medium text-accent hover:underline"
                          onClick={(event) => {
                            if (!onOpenSocio || event.metaKey || event.ctrlKey) {
                              return;
                            }
                            event.preventDefault();
                            onOpenSocio(socio.nome, socio.documento);
                          }}
                        >
                          {socio.nome}
                          <ArrowUpRight className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
                        </Link>
                      ) : (
                        <span className="font-medium text-foreground">—</span>
                      )}
                      {socio.representante_nome ? (
                        <span className="block text-xs text-muted-foreground">
                          Repr.: {socio.representante_nome}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5 align-top text-foreground">
                      {socio.qualificacao_descricao ?? "—"}
                    </td>
                    <td className="px-3 py-2.5 align-top text-foreground">
                      {labelFor(IDENTIFICADOR_SOCIO_LABELS, socio.identificador)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 align-top text-foreground">
                      {formatDateBR(socio.data_entrada)}
                    </td>
                    <td className="px-3 py-2.5 align-top text-foreground">
                      {labelFor(FAIXA_ETARIA_LABELS, socio.faixa_etaria)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Nenhum sócio informado.</p>
        )}
      </Section>

      {/* Outros estabelecimentos */}
      {outros_estabelecimentos.length > 0 ? (
        <Section
          title={`Outros estabelecimentos (${outros_estabelecimentos.length})`}
          icon={<Building2 className="h-4 w-4" aria-hidden="true" />}
        >
          <ul className="divide-y divide-border">
            {outros_estabelecimentos.map((other) => (
              <li
                key={other.cnpj}
                className="flex flex-wrap items-center justify-between gap-2 py-2"
              >
                <div className="min-w-0">
                  <Link
                    to={`/cnpj/${other.cnpj}`}
                    state={{ from }}
                    className="font-mono text-sm text-accent hover:underline"
                    onClick={(event) => {
                      if (!onOpenCnpj || event.metaKey || event.ctrlKey) {
                        return;
                      }
                      event.preventDefault();
                      onOpenCnpj(other.cnpj);
                    }}
                  >
                    {formatCnpj(other.cnpj)}
                  </Link>
                  <span className="ml-2 text-sm text-muted-foreground">
                    {other.nome_fantasia || labelFor(MATRIZ_FILIAL_LABELS, other.matriz_filial)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {other.municipio_nome} / {other.uf}
                  </span>
                  <SituacaoBadge codigo={other.situacao_cadastral} />
                </div>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </div>
  );
}
